import { useMemo, useState } from "react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Card from "@mui/material/Card";
import ToggleButton from "@mui/material/ToggleButton";
import ToggleButtonGroup from "@mui/material/ToggleButtonGroup";
import AddIcon from "@mui/icons-material/AddOutlined";
import ListViewIcon from "@mui/icons-material/ViewListOutlined";
import TreeViewIcon from "@mui/icons-material/AccountTreeOutlined";
import CardViewIcon from "@mui/icons-material/GridViewOutlined";
import ArchiveIcon from "@mui/icons-material/Inventory2Outlined";
import DeleteIcon from "@mui/icons-material/DeleteOutlined";
import DriveFileMoveIcon from "@mui/icons-material/DriveFileMoveOutlined";
import { PageContainer, PageHeader } from "@/components/layout/page";
import { DataTable, type Column } from "@/components/data/data-table";
import { FilterSelect } from "@/components/data/filter-select";
import { ShowArchivedToggle } from "@/components/common/show-archived-toggle";
import { BulkActionBar } from "@/components/common/bulk-action-bar";
import { useRowSelection } from "@/components/common/use-row-selection";
import { MoveToWorkspaceDialog } from "@/components/common/move-to-workspace-dialog";
import { useToast } from "@/components/feedback/toaster";
import { useConfirm } from "@/components/feedback/confirm-dialog";
import { StatusChip } from "@/components/display/status-chip";
import { RelativeTime } from "@/components/display/relative-time";
import { EmptyState } from "@/components/feedback/states";
import { ConnectorIcon } from "@/components/connectors/connector-icon";
import { ConnectorTree } from "@/components/connectors/connector-tree";
import { ConnectorCards } from "@/components/connectors/connector-cards";
import {
  type Connector,
  useBulkConnectors,
  useConnectors,
  useConnectorsPage,
  useConnectorTypes,
} from "@/api/connectors";
import { useTableParams } from "@/lib/table-params";

type View = "list" | "tree" | "cards";
const VIEW_KEY = "udip.connectors.view";

function readView(): View {
  try {
    const v = localStorage.getItem(VIEW_KEY);
    if (v === "list" || v === "tree" || v === "cards") return v;
  } catch {
    /* ignore */
  }
  return "list";
}

export const Route = createFileRoute("/_app/connectors/")({
  component: ConnectorsList,
});

function ConnectorsList() {
  const navigate = useNavigate();
  const toast = useToast();
  const confirm = useConfirm();
  const params = useTableParams({ sort: { id: "createdAt", dir: "desc" } });
  const { data, isLoading, error, refetch } = useConnectorsPage(params);
  const sel = useRowSelection();
  const bulk = useBulkConnectors();
  const [bulkMoveOpen, setBulkMoveOpen] = useState(false);

  async function runBulk(
    op: "archive" | "unarchive" | "delete" | "move",
    workspaceId?: string | null,
  ) {
    const res = await bulk.mutateAsync({ ids: sel.ids, op, workspaceId });
    sel.clear();
    toast.success(
      res.failed.length ? `${res.ok} done, ${res.failed.length} failed` : `${res.ok} updated`,
    );
  }
  const types = useConnectorTypes().data;
  const typeOptions = useMemo(() => (types ?? []).map((t) => t.type).sort(), [types]);

  const [view, setView] = useState<View>(readView);
  const all = useConnectors();
  const openConnector = (id: string) =>
    navigate({ to: "/connectors/$connectorId", params: { connectorId: id } });
  function changeView(next: View | null) {
    if (!next) return;
    setView(next);
    try {
      localStorage.setItem(VIEW_KEY, next);
    } catch {
      /* ignore */
    }
  }

  const columns: Column<Connector>[] = [
    {
      id: "name",
      header: "Name",
      cell: (r) => (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <ConnectorIcon type={r.type} fontSize="small" />
          {r.name}
        </Box>
      ),
      sortValue: (r) => r.name,
    },
    { id: "type", header: "Type", cell: (r) => r.type, nowrap: true, sortValue: (r) => r.type },
    { id: "kind", header: "Kind", cell: (r) => r.kind ?? "STANDALONE", nowrap: true },
    { id: "status", header: "Status", cell: (r) => <StatusChip status={r.status ?? "UNKNOWN"} /> },
    {
      id: "lastTestedAt",
      header: "Last tested",
      cell: (r) => <RelativeTime value={r.lastTestedAt} />,
      sortValue: (r) => r.lastTestedAt,
    },
  ];

  return (
    <PageContainer>
      <PageHeader
        title="Connectors"
        description="Reusable connections to source and destination systems."
        actions={
          <>
            <ToggleButtonGroup
              size="small"
              exclusive
              value={view}
              onChange={(_, v: View | null) => changeView(v)}
            >
              <ToggleButton value="list" aria-label="List view">
                <ListViewIcon fontSize="small" />
              </ToggleButton>
              <ToggleButton value="tree" aria-label="Tree view">
                <TreeViewIcon fontSize="small" />
              </ToggleButton>
              <ToggleButton value="cards" aria-label="Card view">
                <CardViewIcon fontSize="small" />
              </ToggleButton>
            </ToggleButtonGroup>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => navigate({ to: "/connectors/new" })}
            >
              New connector
            </Button>
          </>
        }
      />
      {view === "list" ? (
        <BulkActionBar
          count={sel.count}
          onClear={sel.clear}
          actions={[
            {
              label: "Move to workspace…",
              icon: <DriveFileMoveIcon fontSize="small" />,
              onClick: () => setBulkMoveOpen(true),
            },
            {
              label: "Archive",
              icon: <ArchiveIcon fontSize="small" />,
              onClick: () => runBulk("archive"),
            },
            {
              label: "Delete",
              icon: <DeleteIcon fontSize="small" />,
              danger: true,
              onClick: async () => {
                if (
                  await confirm({
                    title: `Delete ${sel.count} connector${sel.count === 1 ? "" : "s"}?`,
                    destructive: true,
                  })
                ) {
                  await runBulk("delete");
                }
              },
            },
          ]}
        />
      ) : null}
      <Card>
        {view === "tree" ? (
          <ConnectorTree
            connectors={all.data ?? []}
            isLoading={all.isLoading}
            onSelect={openConnector}
          />
        ) : view === "cards" ? (
          <ConnectorCards
            connectors={all.data ?? []}
            isLoading={all.isLoading}
            onSelect={openConnector}
          />
        ) : (
          <DataTable
            columns={columns}
            data={data?.content}
            total={data?.totalElements}
            params={params}
            selection={sel.api}
            isLoading={isLoading}
            error={error}
            onRetry={() => refetch()}
            rowKey={(r) => r.id}
            searchPlaceholder="Search connectors…"
            filters={
              <>
                <FilterSelect
                  label="Type"
                  value={params.filters.type ?? ""}
                  onChange={(v) => params.setFilter("type", v)}
                  options={typeOptions}
                />
                <FilterSelect
                  label="Status"
                  value={params.filters.status ?? ""}
                  onChange={(v) => params.setFilter("status", v)}
                  options={["CONNECTED", "ERROR", "UNTESTED", "DRAFT"]}
                />
                <ShowArchivedToggle params={params} />
              </>
            }
            onRowClick={(r) =>
              navigate({ to: "/connectors/$connectorId", params: { connectorId: r.id } })
            }
            empty={
              <EmptyState
                title="No connectors yet"
                description="Add a connector so pipelines have something to read from and write to."
                action={
                  <Button variant="contained" onClick={() => navigate({ to: "/connectors/new" })}>
                    New connector
                  </Button>
                }
              />
            }
          />
        )}
      </Card>

      <MoveToWorkspaceDialog
        open={bulkMoveOpen}
        onClose={() => setBulkMoveOpen(false)}
        noun={`${sel.count} connector${sel.count === 1 ? "" : "s"}`}
        currentWorkspaceId={null}
        onMove={(workspaceId) => runBulk("move", workspaceId)}
      />
    </PageContainer>
  );
}
